import { Controller } from "@hotwired/stimulus"

const ROW = "[data-pending-rows-row]"
const DRAGGING = ["opacity-40"]
const TARGETED = ["ring-2", "ring-orange-400", "ring-inset"]

export default class extends Controller {
  start(event) {
    const row = event.target.closest(ROW)
    if (!row || !this.placedRows().includes(row)) return event.preventDefault()

    event.dataTransfer.effectAllowed = "move"
    event.dataTransfer.setData("text/plain", row.dataset.columnPosition)
    this.source = row
    row.classList.add(...DRAGGING)
  }

  over(event) {
    const row = event.currentTarget
    if (!this.source || row === this.source || !this.placedRows().includes(row)) return

    event.preventDefault()
    event.dataTransfer.dropEffect = "move"
    row.classList.add(...TARGETED)
  }

  leave(event) {
    event.currentTarget.classList.remove(...TARGETED)
  }

  drop(event) {
    event.preventDefault()
    event.currentTarget.classList.remove(...TARGETED)

    const target = event.currentTarget
    const rows = this.placedRows()
    const from = rows.indexOf(this.source)
    const to = rows.indexOf(target)
    if (from < 0 || to < 0 || from === to) return

    const coordinates = rows.map((row) => Number(row.dataset.columnPosition))
    const anchor = rows[rows.length - 1].nextSibling
    const order = rows.slice()
    order.splice(from, 1)
    order.splice(order.indexOf(target) + (from < to ? 1 : 0), 0, this.source)

    this.reseat(order, coordinates, anchor)
    this.markDirty()
  }

  finish() {
    this.element.querySelectorAll(ROW).forEach((row) => {
      row.classList.remove(...DRAGGING, ...TARGETED)
    })
    this.source = null
  }

  insertAbove(event) {
    this.insert(event, 0)
  }

  insertBelow(event) {
    this.insert(event, 1)
  }

  insert(event, offset) {
    const anchorRow = event.target.closest(ROW)
    const rows = this.placedRows()
    if (!rows.includes(anchorRow)) return

    const at = Number(anchorRow.dataset.columnPosition) + offset
    rows.forEach((row) => {
      const position = Number(row.dataset.columnPosition)
      if (position >= at) this.place(row, position + 1)
    })

    const before = offset === 0 ? anchorRow : anchorRow.nextSibling
    const parent = anchorRow.parentNode
    const added = this.pendingRows()?.addRow()
    if (!added) return

    parent.insertBefore(added, before)
    this.place(added, at)
    added.querySelector("[data-test-id='column-name']")?.focus()
  }

  reseat(order, coordinates, anchor) {
    const parent = order[0].parentNode
    order.forEach((row, index) => {
      parent.insertBefore(row, anchor)
      this.place(row, coordinates[index])
    })
  }

  place(row, position) {
    row.dataset.columnPosition = position

    const field = row.querySelector("[data-column-position-field]")
    if (field) field.value = position

    const label = row.querySelector("[data-column-position-label]")
    if (label) label.textContent = position

    const type = row.querySelector("[data-test-id='column-data-type']")
    if (type) type.dataset.columnTypesPositionParam = position
  }

  placedRows() {
    const rows = this.pendingRows()?.visibleRows() || []
    return rows.filter((row) => row.dataset.columnPosition)
  }

  pendingRows() {
    return this.application.getControllerForElementAndIdentifier(this.element, "pending-rows")
  }

  markDirty() {
    const host = this.element.closest("[data-controller~='dirty-guard']")
    if (!host) return

    this.application.getControllerForElementAndIdentifier(host, "dirty-guard")?.markDirty()
  }
}
